import React, { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Play, CheckCircle, XCircle, AlertCircle, Loader2 } from 'lucide-react'

interface TestCase {
  input: string
  expected: 'pass' | 'fail'
  description: string
}

interface TestResult {
  testCase: TestCase
  actual: 'pass' | 'fail'
  passed: boolean
  details?: string
}

interface TestRunnerProps {
  ruleId: number | string
  rule: string
  field: string
  testCases: TestCase[]
  onTestComplete?: (results: TestResult[]) => void
}

export default function TestRunner({ ruleId, rule, field, testCases, onTestComplete }: TestRunnerProps) {
  const [running, setRunning] = useState(false)
  const [results, setResults] = useState<TestResult[]>([])
  const [showResults, setShowResults] = useState(false)

  const runTests = async () => {
    setRunning(true)
    setShowResults(true)
    
    // Simulate running tests (in real implementation, this would call validation logic)
    const testResults: TestResult[] = []
    
    for (const testCase of testCases) {
      await new Promise(resolve => setTimeout(resolve, 300)) // Simulate async test
      
      // Run the actual validation logic
      const actual = validateInput(testCase.input, field, rule)
      const passed = actual === testCase.expected
      
      testResults.push({
        testCase,
        actual,
        passed,
        details: passed ? 'Test passed as expected' : `Expected ${testCase.expected} but got ${actual}`
      })
    }
    
    setResults(testResults)
    setRunning(false)
    
    if (onTestComplete) {
      onTestComplete(testResults)
    }
  }

  // Basic validation logic (can be expanded)
  const validateInput = (input: string, field: string, rule: string): 'pass' | 'fail' => {
    if (field === 'dimensions') {
      // Check if dimensions are in the allowed list from the rule
      const allowedDimensions = ['160x600', '300x250', '728x90', '300x600']
      return allowedDimensions.includes(input) ? 'pass' : 'fail'
    }
    
    if (field === 'fileSize') {
      const sizeInKB = parseInt(input.replace('KB', ''))
      return sizeInKB <= 150 ? 'pass' : 'fail'
    }
    
    if (field === 'format') {
      const allowedFormats = ['JPG', 'PNG', 'GIF', 'MP4', 'MOV']
      return allowedFormats.includes(input.toUpperCase()) ? 'pass' : 'fail'
    }
    
    if (field === 'duration') {
      const duration = parseInt(input.replace('s', ''))
      return duration >= 6 && duration <= 30 ? 'pass' : 'fail'
    }
    
    // Default: return pass for now
    return 'pass'
  }

  const passedCount = results.filter(r => r.passed).length
  const failedCount = results.length - passedCount
  const successRate = results.length > 0 ? Math.round((passedCount / results.length) * 100) : 0

  return (
    <div className="space-y-4">
      {/* Run Tests Button */}
      <div className="flex items-center justify-between">
        <Button
          onClick={runTests}
          disabled={running}
          variant="outline"
          size="sm"
          className="gap-2"
        >
          {running ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Running Tests...
            </>
          ) : (
            <>
              <Play className="w-4 h-4" />
              Run All Tests ({testCases.length})
            </>
          )}
        </Button>

        {results.length > 0 && (
          <div className="text-sm">
            <span className="text-green-600 font-medium">{passedCount} passed</span>
            {failedCount > 0 && (
              <span className="text-red-600 font-medium ml-3">{failedCount} failed</span>
            )}
            <span className="text-muted-foreground ml-3">({successRate}% success)</span>
          </div>
        )}
      </div>

      {/* Test Results */}
      {showResults && results.length > 0 && (
        <div className="space-y-2 border rounded-lg p-4 bg-muted/30">
          <h4 className="font-semibold text-sm mb-3">Test Results</h4>
          {results.map((result, index) => (
            <div
              key={index}
              className={`flex items-start gap-3 p-3 rounded-lg ${
                result.passed 
                  ? 'bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-900' 
                  : 'bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900'
              }`}
            >
              {result.passed ? (
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
              ) : (
                <XCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
              )}
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <code className={`text-sm font-mono px-2 py-0.5 rounded ${
                    result.passed ? 'bg-green-100 dark:bg-green-900/30' : 'bg-red-100 dark:bg-red-900/30'
                  }`}>
                    {result.testCase.input}
                  </code>
                  <span className={`text-xs font-medium ${
                    result.passed ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'
                  }`}>
                    {result.passed ? 'PASS' : 'FAIL'}
                  </span>
                </div>
                
                <p className="text-sm text-muted-foreground mt-1">
                  {result.testCase.description}
                </p>
                
                {!result.passed && result.details && (
                  <p className="text-xs text-red-600 dark:text-red-400 mt-1 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {result.details}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

